import { useCallback, useEffect, useState } from 'react';
import { backend } from '../data';
import { BhStore, type AdminActionResult, type AdminFamily, type AdminUser, type Session } from '@bhorosha/data';
import { t, tErr } from '@bhorosha/i18n';
import { BrandMark, ErrorText, Field, Notice, Spinner, initials } from '../ui/components';
import { downloadJson, exportPeople, importPeople, parsePeopleFile, summarize, type PeopleFile } from '../personTransfer';
import { inviteEmail, tempPasswordMessage } from '../inviteEmail';
import { FamiliesTab } from './AdminFamilies';
import { NewFamilyWizard } from './AdminWelcome';
import { LibraryTab } from './AdminLibrary';
import { fmtDMYTime } from '../ui/dates';

type Tab = 'users' | 'families' | 'library' | 'transfer';
type P = { id: string; name: string };

async function peopleOf(familyId: string): Promise<P[]> {
  const snap = await backend.loadFamilyData(familyId);
  return snap.rows.filter(r => r.table === 'patients').sort((a, b) => a.position - b.position)
    .map(r => ({ id: r.id, name: String((r.data as { name?: string })?.name || 'Unnamed') }));
}

async function copy(text: string) {
  try { await navigator.clipboard.writeText(text); return true; }
  catch { prompt('Copy this text', text); return false; }
}

/**
 * The separate super admin login opens this page instead of the family app:
 * add users and send them invite emails, activate / deactivate, manage every
 * family's owner, editors, viewers and phone logins, import / export people.
 */
export function AdminConsole({ session }: { session: Session }) {
  const [tab, setTab] = useState<Tab>('users');
  const [families, setFamilies] = useState<AdminFamily[] | null>(null);
  const [err, setErr] = useState('');
  const loadFamilies = useCallback(() => backend.adminListFamilies().then(setFamilies).catch(e => setErr(tErr(e))), []);
  useEffect(() => { void loadFamilies(); }, [loadFamilies]);

  async function signOut() {
    BhStore.clearDeviceCache(localStorage);
    await backend.signOut();
    location.href = '/';
  }

  return (
    <div className="bh-console">
      <header className="bh-console-top">
        <div className="bh-console-brand"><BrandMark size={32} /> <b>Bhorosha</b> <span className="bh-console-tag">{t('adm.consoleTitle')}</span></div>
        <div className="bh-console-me">
          <a className="bh-console-link" href="/manual" target="_blank" rel="noreferrer">{t('adm.manual')}</a>
          <span className="bh-small">{session.email}</span>
          <button className="bh-btn bh-btn-ghost bh-btn-sm" onClick={signOut}>{t('menu.signOut')}</button>
        </div>
      </header>
      <main className="bh-console-main">
        <nav className="bh-console-tabs" role="tablist" aria-label={t('adm.consoleTitle')}>
          {([['users', '👥', 'adm.tab.users', 'adm.tab.usersSub'], ['families', '🏠', 'adm.tab.families', 'adm.tab.familiesSub'], ['library', '💊', 'adm.tab.library', 'adm.tab.librarySub'], ['transfer', '💾', 'adm.tab.transfer', 'adm.tab.transferSub']] as const).map(([id, icon, label, sub]) => (
            <button key={id} role="tab" aria-selected={tab === id} className={tab === id ? 'on' : ''} onClick={() => setTab(id)}>
              <span className="bh-ctab-icon" aria-hidden="true">{icon}</span>
              <span className="bh-ctab-text"><b>{t(label)}</b><small>{t(sub)}</small></span>
            </button>
          ))}
        </nav>
        <ErrorText>{err}</ErrorText>
        {families === null ? <Spinner /> : tab === 'users'
          ? <UsersTab session={session} families={families} onChanged={loadFamilies} />
          : tab === 'library' ? <LibraryTab />
          : tab === 'families' ? <FamiliesTab families={families} onChanged={loadFamilies} adminName={session.displayName} />
          : <TransferTab session={session} families={families} onChanged={loadFamilies} />}
      </main>
    </div>
  );
}

// ------------------------------------------------------------------ invite email card
function InviteCard({ user, invite, adminName, onClose }: { user: { name: string; email: string }; invite: AdminActionResult; adminName: string; onClose: () => void }) {
  const mail = inviteEmail({ name: user.name, email: user.email, token: invite.token!, expiresAt: invite.expiresAt!, adminName });
  const [done, setDone] = useState('');
  const flash = (m: string) => { setDone(m); setTimeout(() => setDone(''), 1800); };
  return (
    <section className="bh-invite-card" aria-label={t('adm.inviteReady')}>
      <div className="bh-invite-head-row">
        <div>
          <h3 className="bh-h3" style={{ margin: 0 }}>✉️ {t('adm.inviteReady')}</h3>
          <p className="bh-small bh-muted" style={{ margin: '2px 0 0' }}>{t('adm.inviteHelp', { email: user.email })}</p>
        </div>
        <button className="bh-icon-btn" onClick={onClose} aria-label={t('common.close')}>&times;</button>
      </div>

      <div className="bh-mail-preview">
        <div className="bh-mail-meta"><span>{t('adm.mailTo')}</span><b>{user.email}</b></div>
        <div className="bh-mail-meta"><span>{t('adm.mailSubject')}</span><b>{mail.subject}</b></div>
        <div className="bh-mail-body">
          <p>Hi {user.name || 'there'},</p>
          <p>You have been given an account on <b>Bhorosha</b> — one place for your family's medicines, injection reminders, doctor visits and test results.</p>
          <div className="bh-mail-step">
            <span className="bh-mail-num">1</span>
            <div>
              <b>Set your password and sign in</b>
              <a className="bh-mail-btn" href={mail.link} target="_blank" rel="noreferrer">Set my password</a>
              <a className="bh-mail-url" href={mail.link} target="_blank" rel="noreferrer">{mail.link}</a>
              <span className="bh-small bh-muted">Works once, until {mail.expires}.</span>
            </div>
          </div>
          <div className="bh-mail-step">
            <span className="bh-mail-num">2</span>
            <div>
              <b>New to Bhorosha? Read the short guide</b>
              <a className="bh-mail-url" href={mail.manual} target="_blank" rel="noreferrer">{mail.manual}</a>
            </div>
          </div>
          <p>Your sign-in email: <b>{user.email}</b></p>
          <p className="bh-muted">If the link has expired, just reply and I'll send you a new one.</p>
          <p>— {adminName ? `${adminName}, ` : ''}Bhorosha admin</p>
        </div>
      </div>

      <div className="bh-row bh-invite-actions">
        <button className="bh-btn bh-btn-primary" onClick={async () => { await copy(mail.full); flash(t('adm.copiedEmail')); }}>📋 {t('adm.copyEmail')}</button>
        <button className="bh-btn bh-btn-ghost" onClick={async () => { await copy(mail.link); flash(t('common.copied')); }}>🔗 {t('adm.copyLink')}</button>
        <a className="bh-btn bh-btn-ghost" href={`mailto:${encodeURIComponent(user.email)}?subject=${encodeURIComponent(mail.subject)}&body=${encodeURIComponent(mail.body)}`}>✉️ {t('adm.openEmail')}</a>
        {typeof navigator.share === 'function' && <button className="bh-btn bh-btn-ghost" onClick={() => navigator.share({ title: mail.subject, text: mail.body }).catch(() => {})}>📤 {t('common.share')}</button>}
        {done && <span className="bh-small bh-ok">✓ {done}</span>}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ one-time password card
function TempPasswordCard({ user, result, inviteExpiresAt, adminName, onClose }: { user: { name: string; email: string }; result: AdminActionResult; inviteExpiresAt: string | null; adminName: string; onClose: () => void }) {
  const msg = tempPasswordMessage({ name: user.name, email: user.email, password: result.tempPassword!, expiresAt: result.expiresAt!, adminName });
  const [done, setDone] = useState('');
  const flash = (m: string) => { setDone(m); setTimeout(() => setDone(''), 1800); };
  return (
    <section className="bh-invite-card bh-temp-card" aria-label={t('adm.tempReady', { name: user.name })}>
      <div className="bh-invite-head-row">
        <div>
          <h3 className="bh-h3" style={{ margin: 0 }}>🔑 {t('adm.tempReady', { name: user.name || user.email })}</h3>
          <p className="bh-small bh-muted" style={{ margin: '2px 0 0' }}>{t('adm.tempHelp', { date: msg.expires, name: user.name || user.email })}</p>
        </div>
        <button className="bh-icon-btn" onClick={onClose} aria-label={t('common.close')}>&times;</button>
      </div>
      <div className="bh-temp-pw-row">
        <code className="bh-temp-pw" aria-label="One-time password">{result.tempPassword}</code>
        <button className="bh-btn bh-btn-ghost" onClick={async () => { await copy(result.tempPassword!); flash(t('adm.copiedPw')); }}>📋 {t('adm.copyPw')}</button>
      </div>
      {inviteExpiresAt && new Date(inviteExpiresAt) > new Date() && (
        <p className="bh-small bh-muted">{t('adm.tempStillLink', { date: fmtDMYTime(inviteExpiresAt) })}</p>
      )}
      <div className="bh-mail-preview">
        <div className="bh-mail-meta"><span>{t('adm.mailTo')}</span><b>{user.email}</b></div>
        <div className="bh-mail-meta"><span>{t('adm.mailSubject')}</span><b>{msg.subject}</b></div>
        <div className="bh-mail-body">
          <p>Hi {user.name || 'there'},</p>
          <p>Your Bhorosha account is ready. Sign in here:</p>
          <a className="bh-mail-btn" href={msg.site} target="_blank" rel="noreferrer">Sign in to Bhorosha</a>
          <a className="bh-mail-url" href={msg.site} target="_blank" rel="noreferrer">{msg.site}</a>
          <p>Email: <b>{user.email}</b><br />One-time password: <b className="bh-mono">{result.tempPassword}</b></p>
          <p className="bh-muted">This password works until {msg.expires}. After you sign in, you'll choose your own password.</p>
          <p>New to Bhorosha? Read the short guide first:<br /><a className="bh-mail-url" href={msg.manual} target="_blank" rel="noreferrer">{msg.manual}</a></p>
          <p>— {adminName ? `${adminName}, ` : ''}Bhorosha admin</p>
        </div>
      </div>
      <div className="bh-row bh-invite-actions">
        <button className="bh-btn bh-btn-primary" onClick={async () => { await copy(msg.full); flash(t('adm.copiedMsg')); }}>📋 {t('adm.copyMsg')}</button>
        <a className="bh-btn bh-btn-ghost" href={`mailto:${encodeURIComponent(user.email)}?subject=${encodeURIComponent(msg.subject)}&body=${encodeURIComponent(msg.body)}`}>✉️ {t('adm.openEmail')}</a>
        {typeof navigator.share === 'function' && <button className="bh-btn bh-btn-ghost" onClick={() => navigator.share({ title: msg.subject, text: msg.body }).catch(() => {})}>📤 {t('common.share')}</button>}
        {done && <span className="bh-small bh-ok">✓ {done}</span>}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ users
const STATUS_CLASS: Record<string, string> = { active: 'bh-chip-on', invited: 'bh-chip-wait', deactivated: 'bh-chip-off', temp_password: 'bh-chip-wait' };
const shortDate = (iso: string) => fmtDMYTime(iso);

function UsersTab({ session, families, onChanged }: { session: Session; families: AdminFamily[]; onChanged: () => void }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [invite, setInvite] = useState<{ user: { name: string; email: string }; result: AdminActionResult } | null>(null);
  const [temp, setTemp] = useState<{ user: { name: string; email: string }; result: AdminActionResult; inviteExpiresAt: string | null } | null>(null);
  const [filter, setFilter] = useState('');

  const reload = useCallback(() => backend.adminListUsers().then(setUsers).catch(e => setErr(tErr(e))), []);
  useEffect(() => { void reload(); }, [reload]);

  async function act(fn: () => Promise<unknown>, ok: string) {
    setErr(''); setMsg('');
    try { await fn(); setMsg(ok); await reload(); onChanged(); } catch (e) { setErr(tErr(e)); }
  }

  async function reinvite(u: AdminUser) {
    setErr(''); setMsg('');
    try {
      const r = await backend.adminUserAction({ action: 'reinvite', userId: u.userId });
      setTemp(null);
      setInvite({ user: { name: u.displayName, email: u.email }, result: r });
      await reload();
      setTimeout(() => document.querySelector('.bh-invite-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
    } catch (e) { setErr(tErr(e)); }
  }

  /** Activate (gives a one-time password if they have none) or reset the password. */
  async function givePassword(u: AdminUser, action: 'activate' | 'reset_password') {
    if (action === 'reset_password' && !confirm(t('adm.resetConfirm', { name: u.displayName }))) return;
    setErr(''); setMsg('');
    try {
      const r = await backend.adminUserAction({ action, userId: u.userId });
      if (r.tempPassword) {
        setInvite(null);
        setTemp({ user: { name: u.displayName, email: u.email }, result: r, inviteExpiresAt: u.inviteExpiresAt });
        setTimeout(() => document.querySelector('.bh-temp-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      } else setMsg(t('adm.activated', { name: u.displayName }));
      await reload(); onChanged();
    } catch (e) { setErr(tErr(e)); }
  }

  const shown = (users || []).filter(u => !filter || `${u.displayName} ${u.email} ${u.families}`.toLowerCase().includes(filter.toLowerCase()));
  return (
    <div>
      <div className="bh-admin-grid">
        <div className="bh-admin-col">
        {/* Add a user = a new family for them, they own it, plus (optionally) the phone user, then the welcome email */}
        <div className="bh-admin-card"><NewFamilyWizard adminName={session.displayName} onCreated={() => { void reload(); onChanged(); }} /></div>
        </div>

        <div className="bh-admin-card">
          <div className="bh-row bh-users-head" style={{ justifyContent: 'space-between' }}>
            <h3 className="bh-h3">{t('adm.allUsers')} {users ? `(${users.length})` : ''}</h3>
            <input className="bh-search" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('adm.search')} aria-label={t('adm.search')} />
          </div>
          {msg && <Notice tone="ok">{msg}</Notice>}
          <ErrorText>{err}</ErrorText>
          {!users ? <Spinner /> : (
            <div className="bh-list">
              {!shown.length && <p className="bh-small bh-muted">{t('adm.noUsers')}</p>}
              {shown.map(u => {
                const me = u.userId === session.userId;
                return (
                  <div className="bh-member" key={u.userId}>
                    <span className="bh-acct-avatar">{initials(u.displayName || u.email)}</span>
                    <div className="bh-member-main">
                      <div className="bh-strong">{u.displayName} {me && <span className="bh-chip">{t('fam.you')}</span>}
                        {u.isAdmin && <span className="bh-chip bh-chip-admin">{t('adm.superAdmin')}</span>}
                        {!u.isAdmin && <span className={`bh-chip ${STATUS_CLASS[u.status]}`}>{t(`adm.status.${u.status}`)}</span>}</div>
                      <div className="bh-small bh-muted">{u.email}</div>
                      <div className="bh-small">{u.isAdmin ? t('adm.adminAccount') : (u.families || t('adm.noFamilyYet'))}</div>
                      {u.status === 'temp_password' && u.tempExpiresAt && <div className={`bh-small ${new Date(u.tempExpiresAt) < new Date() ? 'bh-warn' : 'bh-muted'}`}>{new Date(u.tempExpiresAt) < new Date() ? t('adm.tempExpired') : t('adm.tempExpires', { date: shortDate(u.tempExpiresAt) })}</div>}
                      {(u.status === 'invited' || u.status === 'temp_password') && u.inviteExpiresAt && new Date(u.inviteExpiresAt) > new Date() && <div className="bh-small bh-muted">{t('adm.linkExpires', { date: shortDate(u.inviteExpiresAt) })}</div>}
                    </div>
                    {!me && !u.isAdmin && (
                      <div className="bh-member-side">
                        {u.status !== 'active' && <button className="bh-link" onClick={() => reinvite(u)}>{t('adm.newLink')}</button>}
                        {u.status === 'active' && <button className="bh-link" onClick={() => givePassword(u, 'reset_password')}>{t('adm.resetPw')}</button>}
                        {u.status === 'temp_password' && <button className="bh-link" onClick={() => givePassword(u, 'reset_password')}>{t('adm.newTemp')}</button>}
                        {u.status === 'invited' || u.status === 'deactivated'
                          ? <button className="bh-link" onClick={() => givePassword(u, 'activate')}>{t('adm.activate')}</button>
                          : <button className="bh-link" onClick={() => act(() => backend.adminUserAction({ action: 'deactivate', userId: u.userId }), t('adm.deactivated', { name: u.displayName }))}>{t('adm.deactivate')}</button>}
                        <button className="bh-link bh-warn" onClick={() => {
                          if (confirm(t('adm.deleteConfirm', { name: u.displayName }))) void act(() => backend.adminUserAction({ action: 'delete', userId: u.userId }), t('fam.saved'));
                        }}>{t('adm.delete')}</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {temp?.result.tempPassword && <TempPasswordCard user={temp.user} result={temp.result} inviteExpiresAt={temp.inviteExpiresAt} adminName={session.displayName} onClose={() => setTemp(null)} />}
      {invite?.result.token && <InviteCard user={invite.user} invite={invite.result} adminName={session.displayName} onClose={() => setInvite(null)} />}
    </div>
  );
}

// ------------------------------------------------------------------ import / export
function TransferTab({ session, families, onChanged }: { session: Session; families: AdminFamily[]; onChanged: () => void }) {
  // export
  const [exFamily, setExFamily] = useState(families[0]?.familyId || '');
  const [exPeople, setExPeople] = useState<P[] | null>(null);
  const [exSel, setExSel] = useState<string[]>([]);
  // import
  const [file, setFile] = useState<PeopleFile | null>(null);
  const [fileName, setFileName] = useState('');
  const [imSel, setImSel] = useState<number[]>([]);
  const [imFamily, setImFamily] = useState(families[0]?.familyId || '');
  const [imPeople, setImPeople] = useState<P[]>([]);
  const [mode, setMode] = useState<'new' | 'replace'>('new');
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!exFamily) return;
    setExPeople(null);
    peopleOf(exFamily).then(p => { setExPeople(p); setExSel(p.map(x => x.id)); }).catch(e => setErr(tErr(e)));
  }, [exFamily]);
  useEffect(() => {
    if (!imFamily) return;
    peopleOf(imFamily).then(p => { setImPeople(p); setTarget(p[0]?.id || ''); }).catch(e => setErr(tErr(e)));
  }, [imFamily]);

  const famName = (id: string) => families.find(f => f.familyId === id)?.name || 'family';
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  async function doExport() {
    setErr(''); setMsg(''); setBusy(true);
    try {
      const data = await exportPeople(backend, exFamily, famName(exFamily), exSel);
      const who = data.people.length === 1 ? String(data.people[0].person.name || 'person') : famName(exFamily);
      downloadJson(`Bhorosha_${who.replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.json`, data);
      setMsg(t('adm.exported', { n: data.people.length }));
    } catch (e) { setErr(tErr(e)); } finally { setBusy(false); }
  }

  async function pick(f: File | undefined) {
    setErr(''); setMsg(''); setFile(null); setImSel([]);
    if (!f) return;
    const parsed = parsePeopleFile(await f.text());
    if (!parsed || !parsed.people.length) { setErr(t('adm.badFile')); return; }
    setFile(parsed); setFileName(f.name); setImSel(parsed.people.map((_, i) => i));
  }

  async function doImport() {
    if (!file) return;
    if (mode === 'replace') {
      const tName = imPeople.find(p => p.id === target)?.name || '';
      if (!confirm(t('adm.replaceConfirm', { name: tName }))) return;
    }
    setErr(''); setMsg(''); setBusy(true);
    try {
      const names = await importPeople(backend, imFamily, session.userId, file, imSel, mode === 'replace' ? { kind: 'replace', targetId: target } : { kind: 'new' });
      setMsg(t('adm.imported', { names: names.join(', '), family: famName(imFamily) }));
      setImPeople(await peopleOf(imFamily));
      if (exFamily === imFamily) setExPeople(await peopleOf(exFamily));
      onChanged();
    } catch (e) { setErr(tErr(e)); } finally { setBusy(false); }
  }

  if (!families.length) return <Notice>{t('adm.noFamilies')}</Notice>;
  return (
    <div>
      {msg && <Notice tone="ok">{msg}</Notice>}
      <ErrorText>{err}</ErrorText>
      <div className="bh-admin-grid">
        <div className="bh-admin-card bh-form">
          <h3 className="bh-h3">{t('adm.export')}</h3>
          <Field label={t('adm.family')}>
            <select value={exFamily} onChange={e => setExFamily(e.target.value)}>
              {families.map(f => <option key={f.familyId} value={f.familyId}>{f.name} ({f.patients})</option>)}
            </select>
          </Field>
          <Field label={t('adm.people')}>
            {!exPeople ? <Spinner /> : (
              <div className="bh-checks bh-checks-flat">
                {exPeople.length > 1 && <label className="bh-check"><input type="checkbox" checked={exSel.length === exPeople.length}
                  onChange={e => setExSel(e.target.checked ? exPeople.map(p => p.id) : [])} /> <b>{t('adm.everyone')}</b></label>}
                {exPeople.map(p => <label key={p.id} className="bh-check"><input type="checkbox" checked={exSel.includes(p.id)} onChange={() => setExSel(toggle(exSel, p.id))} /> {p.name}</label>)}
                {!exPeople.length && <span className="bh-small bh-muted">{t('adm.noPeople')}</span>}
              </div>
            )}
          </Field>
          <p className="bh-small bh-muted">{t('adm.exportIncludes')}</p>
          <button className="bh-btn bh-btn-primary" disabled={busy || !exSel.length} onClick={doExport}>{t('adm.download')}</button>
        </div>

        <div className="bh-admin-card bh-form">
          <h3 className="bh-h3">{t('adm.import')}</h3>
          <Field label={t('adm.file')} hint={t('adm.fileHint')}>
            <input type="file" accept=".json,application/json" onChange={e => pick(e.target.files?.[0])} />
          </Field>
          {file && (
            <>
              <Field label={t('adm.inFile', { file: fileName })}>
                <div className="bh-checks bh-checks-flat">
                  {file.people.map((b, i) => (
                    <label key={i} className="bh-check"><input type="checkbox" checked={imSel.includes(i)} onChange={() => setImSel(toggle(imSel, i))} />
                      <span><b>{String(b.person.name || 'Unnamed')}</b> <span className="bh-small bh-muted">· {summarize(b)}</span></span></label>
                  ))}
                </div>
              </Field>
              <Field label={t('adm.intoFamily')}>
                <select value={imFamily} onChange={e => setImFamily(e.target.value)}>
                  {families.map(f => <option key={f.familyId} value={f.familyId}>{f.name}</option>)}
                </select>
              </Field>
              <div className="bh-access">
                <label className="bh-radio"><input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} /> {t('adm.asNew')}</label>
                <label className="bh-radio"><input type="radio" checked={mode === 'replace'} disabled={imSel.length !== 1 || !imPeople.length} onChange={() => setMode('replace')} /> {t('adm.replace')}</label>
                {mode === 'replace' && (
                  <select value={target} onChange={e => setTarget(e.target.value)}>
                    {imPeople.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
              </div>
              <button className="bh-btn bh-btn-primary" disabled={busy || !imSel.length || (mode === 'replace' && imSel.length !== 1)} onClick={doImport}>
                {busy ? t('common.loading') : t('adm.importBtn', { n: imSel.length })}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
